/**
 * WizardStepper Component
 * Displays the step progress indicator for the contract wizard
 */

import React, { useMemo } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  styled,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Description as ContractIcon,
  Percent as InterestIcon,
  AccountBalance as FeesIcon,
  Security as SecurityIcon,
  CheckCircle as ReviewIcon,
  Visibility as SummaryIcon,
} from '@mui/icons-material';

// Custom connector styling
const CustomConnector = styled(StepConnector)(({ theme }) => ({
  '& .MuiStepConnector-line': {
    borderTopWidth: 3,
    borderRadius: 1,
  },
  '&.Mui-active .MuiStepConnector-line': {
    borderColor: theme.palette.primary.main,
  },
  '&.Mui-completed .MuiStepConnector-line': {
    borderColor: theme.palette.primary.main,
  },
}));

// Custom step icon styling
const StepIconContainer = styled('div')(({ theme, ownerState }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: ownerState.completed || ownerState.active
    ? theme.palette.primary.main
    : theme.palette.grey[300],
  color: ownerState.completed || ownerState.active
    ? theme.palette.common.white
    : theme.palette.grey[600],
  boxShadow: ownerState.active
    ? `0 0 0 7.2px ${alpha(theme.palette.primary.main, 0.25)}`
    : 'none',
  transition: 'background-color 0.3s, color 0.3s, box-shadow 0.3s',
}));

// Base step configuration (steps 1-5)
const BASE_STEPS = [
  { label: 'Contract Details', icon: <ContractIcon /> },
  { label: 'Interest & Payments', icon: <InterestIcon /> },
  { label: 'Adhoc Events', icon: <FeesIcon /> },
  { label: 'Fees & Security', icon: <SecurityIcon /> },
  { label: 'Review & Schedule', icon: <ReviewIcon /> },
];

// Step 5 - Contract Summary (only shown for non-draft contracts)
const SUMMARY_STEP = { label: 'Contract Summary', icon: <SummaryIcon /> };

// Custom step icon component
function CustomStepIcon({ active, completed, icon, steps }) {
  const stepIndex = Number(icon) - 1;
  const stepConfig = steps[stepIndex];

  return (
    <StepIconContainer ownerState={{ active, completed }}>
      {stepConfig?.icon}
    </StepIconContainer>
  );
}

// `steps` (optional) lets another wizard (the Notes create wizard) reuse this stepper's look
// with its own [{ label, icon }] list; contracts omit it and get the contract steps unchanged.
function WizardStepper({ activeStep, completedSteps = [], onStepClick, showSummaryStep = false, disabled = false, steps: customSteps }) {
  // Compute the steps array based on whether summary step should be shown
  const steps = useMemo(() => {
    if (customSteps) return customSteps;
    if (showSummaryStep) {
      return [...BASE_STEPS, SUMMARY_STEP];
    }
    return BASE_STEPS;
  }, [showSummaryStep, customSteps]);

  // Check if a step is clickable (completed steps can be clicked).
  // Suppressed entirely while `disabled` (a save/schedule round-trip is in flight)
  // so a mid-save click can't fire a second navigation — mirrors the Next button,
  // which disables on the same condition.
  const isStepClickable = (index) => {
    return !disabled && completedSteps.includes(index) && index !== activeStep;
  };

  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      <Stepper
        activeStep={activeStep}
        connector={<CustomConnector />}
        alternativeLabel
      >
        {steps.map((step, index) => {
          const isCompleted = completedSteps.includes(index) || activeStep > index;
          const clickable = isStepClickable(index);

          return (
            <Step
              key={step.label}
              completed={isCompleted}
              sx={{
                cursor: clickable ? 'pointer' : 'default',
                '&:hover': clickable ? {
                  '& .MuiStepLabel-label': {
                    color: 'primary.main',
                  },
                } : {},
              }}
              onClick={() => clickable && onStepClick && onStepClick(index)}
            >
              <StepLabel
                StepIconComponent={(props) => (
                  <CustomStepIcon
                    {...props}
                    active={activeStep === index}
                    completed={isCompleted && activeStep !== index}
                    steps={steps}
                  />
                )}
              >
                {step.label}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
}

// Export both base steps and the function to get all steps
export { BASE_STEPS as STEPS };
export const getSteps = (showSummaryStep) => showSummaryStep ? [...BASE_STEPS, SUMMARY_STEP] : BASE_STEPS;
export default WizardStepper;
